import { WizardStep } from '@/features/missions/components/WizardStep'
import { useMissionsUrl } from '@/features/missions/context/MissionsUrlProvider'
import { useMissionRedirect } from '@/features/missions/hooks/useMissionRedirect'
import { routes } from '@/pages'
import { Button, Spinner } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { useNavigate } from 'react-router-dom'
import {
  useNectarProjectUnauthorized,
  useNectarProjectsUnauthorized,
  useProficiencyAssessmentsByLanguage
} from '../../api'
import { nectarProjectToMission } from '../../api/normalizer'

type ConversationInviteLandingProps = {
  projectId: string
  isAuthorized: boolean
}

export function ConversationInviteLanding({ projectId, isAuthorized }: ConversationInviteLandingProps) {
  const { t } = useLingui()
  const { profileUrl, missionsUrl, assessmentUrl } = useMissionsUrl()
  const navigate = useNavigate()
  const { withRedirectToCurrent } = useMissionRedirect()

  const { data: project, isLoading } = useNectarProjectUnauthorized(projectId)

  const { data: publicProjects = { data: [] } } = useNectarProjectsUnauthorized()
  const assessmentMission = publicProjects.data
    .map((p) => nectarProjectToMission(p, missionsUrl))
    .find((p) => p.type === 'PROFICIENCY_ASSESSMENT')

  const reasons = project?.availability.reasons ?? []
  const profileCompleted = reasons.some((reason) => reason.type === 'profile_required' && reason.completed)
  const assessmentCompleted = reasons.some((reason) => reason.type === 'language_assessment' && reason.completed)
  const hasAssessmentStep = reasons.some((reason) => reason.type === 'language_assessment' && reason.required)

  const { data: assessmentsByLanguage } = useProficiencyAssessmentsByLanguage('en', {
    enabled: isAuthorized && assessmentCompleted
  })
  const assessmentFinalResult = assessmentsByLanguage?.data.find((a) => a.finalResult)?.finalResult ?? null

  const goToLogin = () => {
    const returnTo = `${location.pathname}${location.search}`
    navigate(`${routes.login.path}?redirect=${encodeURIComponent(returnTo)}`)
  }

  const goToProfile = () => {
    navigate(withRedirectToCurrent(profileUrl))
  }

  const goToAssessment = () => {
    if (!assessmentMission) return
    navigate(withRedirectToCurrent(assessmentUrl.replace(':langCode', 'en')))
  }

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-6">
        <Spinner className="mt-8" />
      </div>
    )
  }

  return (
    <div className="max-mobile:max-w-[calc(100vw-40px)] m-auto mt-12 flex w-[720px] flex-col">
      <div className="max-mobile:px-0 relative -mt-12 px-10">
        <div className="max-mobile:text-[22px]/[24px] max-mobile:py-4 max-mobile:pt-12 pt-9 pb-4 text-[28px]/[32px] font-semibold">
          <Trans>
            You are invited to a{' '}
            <span className="text-green max-mobile:max-w-[200px] max-mobile:mt-2 block">conversation!</span>
          </Trans>
        </div>

        <img
          src="/images/conversation-type.png"
          role="presentation"
          className="max-mobile:right-[-10px] absolute right-2 bottom-0 -z-1 w-[210px]"
        />
      </div>
      <div className="bg-background max-mobile:px-4 max-mobile:py-8 flex flex-col items-center justify-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center">
        <div className="flex w-full flex-col items-center gap-3 border-b border-gray-100 pb-7 text-center">
          {project && (
            <div className="flex flex-col items-center justify-center gap-2">
              <div className="text-gray-70 text-subtitle font-semibold">{project.name}</div>
              {project.description && (
                <div className="text-gray-80 text-body3-regular max-w-[500px]">{project.description}</div>
              )}
            </div>
          )}
          <div className="text-gray-80 text-body-2 max-mobile:mt-4 mt-6">
            <Trans>Complete the steps below to join the conversation.</Trans>
          </div>
          {!isAuthorized && (
            <Button
              type="button"
              className="mt-2 w-[300px] cursor-pointer rounded-xl bg-black px-4 py-2 font-semibold text-white"
              onClick={goToLogin}
            >
              <Trans>Sign In to Join</Trans>
            </Button>
          )}
        </div>
        <div className="mt-2 flex flex-col">
          <WizardStep
            projectId={projectId}
            index={1}
            title={t`User Profile`}
            description={t`Tell us a bit about yourself so we can match you with the right missions.`}
            completed={profileCompleted}
            active={!profileCompleted && isAuthorized}
            buttonLabel={t`Complete Profile`}
            buttonDisabled={!isAuthorized}
            onClick={goToProfile}
            buttonType="default"
          />
          {hasAssessmentStep && (
            <WizardStep
              projectId={projectId}
              index={2}
              title={t`English Assessment`}
              description={
                <>
                  <Trans>Take a short English assessment to confirm your eligibility.</Trans>
                  {assessmentCompleted && assessmentFinalResult && (
                    <div className="mt-2">
                      <Trans>Your level:</Trans>{' '}
                      <span className="text-green font-semibold">{assessmentFinalResult.level}</span>
                    </div>
                  )}
                </>
              }
              completed={assessmentCompleted}
              active={profileCompleted && !assessmentCompleted}
              buttonLabel={
                assessmentMission?.view.latestSubmissionStatus === 'IN_PROGRESS'
                  ? t`Continue Assessment`
                  : t`Complete Assessment`
              }
              buttonDisabled={!isAuthorized || !profileCompleted}
              buttonType="default"
              onClick={goToAssessment}
            />
          )}
          <WizardStep
            projectId={projectId}
            index={hasAssessmentStep ? 3 : 2}
            title={t`Join Conversation`}
            description={t`Once the steps above are complete, you will be automatically connected to the conversation.`}
            completed={false}
            active={profileCompleted && (!hasAssessmentStep || assessmentCompleted)}
            buttonType="none"
          />
        </div>
      </div>
    </div>
  )
}
